import { capitalize } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  ControlLabel,
  FormGroup,
  HelpBlock,
  Row,
} from 'react-bootstrap';
import { Field } from 'react-final-form';
import { getOrgTermsContent } from '../../../content/utils.js';
import { ORGANIZATION_ROLES } from '../../../lib/schema.js';
import AuthorizedAction from '../../AuthorizedAction.js';
import CheckboxAdapter from '../../CheckboxAdapter.jsx';
import FieldGroup from '../../FieldGroup.jsx';
import CustomTooltip from '../../CustomTooltip.jsx';
import { FormError } from '../../FormError.jsx';
import { UploadIcon } from '../../icons/UploadIcon.jsx';
import { OrganizationUsersLink } from '../../links/index.jsx';
import QuestionBlueIcon from '../../icons/QuestionBlueIcon.jsx';

const UserForm = (props) => {
  const {
    slug,
    editing,
    userId,
    currentUserId,
    submitError,
    submitFailed,
    submitting,
    handleSubmit,
    handleImageSelect,
    values,
    errors,
  } = props;

  const [showRoles, setShowRoles] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const [fileName, setFileName] = useState('');

  const inputRef = useRef();
  const file = inputRef.current ? inputRef.current.files[0] : null;
  const inputType = (show) => (show ? 'text' : 'password');

  const switchOrgRole = () => {
    if (showRoles) values.orgRole = null;
    setShowRoles(!showRoles);
  };

  useEffect(() => {
    values.orgRole && setShowRoles(true);
  }, [values.orgRole]);

  useEffect(() => {
    if (file) {
      setFileName(file.name);
    }
  }, [file]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
        <h3>About</h3>
      </div>
      <Row className="m-b-28">
        <Col xs={5}>
          <h3 className="flex m-b-28">Information</h3>
          <Field
            id="firstName"
            name="firstName"
            label="First Name"
            placeholder="Enter here"
            type="text"
            component={FieldGroup}
          />
          <Field
            id="userFormLastName"
            name="lastName"
            label="Last Name"
            placeholder="Enter here"
            type="text"
            component={FieldGroup}
          />
          <Field
            id="userFormEmail"
            name="email"
            label="Email"
            placeholder="Enter here"
            type="email"
            component={FieldGroup}
          />
        </Col>

        <Col xs={5} xsOffset={1}>
          {userId !== currentUserId ? (
            <AuthorizedAction action="update" subject="user">
              <h3 className="flex m-b-54">Organization Role</h3>
              <FormGroup>
                <ControlLabel>Role Access</ControlLabel>
                <Field
                  name="admin"
                  className="m-b-46"
                  component={CheckboxAdapter}
                  label={`Click this checkmark to give the User access to all ${getOrgTermsContent('projects')} across the system. `}
                  type="checkbox"
                  checked={showRoles}
                  onClick={switchOrgRole}
                />
              </FormGroup>

              <Field
                id="organizationRole"
                name="orgRole"
                label="Select Role Type"
                componentClass="select"
                customClass={`m-b-8 ${!showRoles && 'hidden'}`}
                component={FieldGroup}
              >
                <option value="" disabled>
                  - Select Role -
                </option>
                {ORGANIZATION_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {capitalize(role)}
                  </option>
                ))}
              </Field>

              {values.orgRole === 'viewer' ? (
                <div className="text-muted m-b-28">
                  Viewer can view all resources within{' '}
                  {getOrgTermsContent('project')} (except stakeholder analysis)
                </div>
              ) : null}

              {values.orgRole === 'admin' ? (
                <div className="text-muted m-b-28">
                  Can view, create, edit all resources, and manage users within
                  organization.
                </div>
              ) : null}

              {values.orgRole === 'consultant' ? (
                <div className="text-muted m-b-28">
                  This role is for third-party Change Management resources.
                  Similar to the Org. Leader role, they can view, edit, manage
                  users, and create resources within the platform.
                </div>
              ) : null}

              {values.orgRole === 'leader' ? (
                <div className="text-muted m-b-28">
                  Can view, create and edit all resources within organization.
                </div>
              ) : null}
            </AuthorizedAction>
          ) : null}
        </Col>
      </Row>

      {editing ? (
        <Row className="m-b-28">
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Additional Information</h3>
          </div>
          <Col xs={5}>
            <h3 className="flex m-b-28">Authentication</h3>
            {userId === currentUserId ? (
              <Row className="m-b-8">
                <Col xs={12}>
                  <ControlLabel>Current Password</ControlLabel>
                </Col>
                <Col xs={10}>
                  <Field
                    id="currentPassword"
                    name="currentPassword"
                    placeholder="Enter Password"
                    type={inputType(showCurrentPassword)}
                    component={FieldGroup}
                  />
                </Col>
                <Col xs={2}>
                  <Button
                    bsStyle="info"
                    className="btn-password btn btn-default"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  />
                </Col>
              </Row>
            ) : null}
            <Row className="m-b-20">
              <Col xs={12} className="flex">
                <ControlLabel className="m-r-8">Password</ControlLabel>
                <CustomTooltip
                  tooltip={
                    <div style={{ maxWidth: '300px' }}>
                      <strong>
                        <p
                          style={{
                            fontSize: '12px',
                            fontWeight: '400',
                            lineHeight: '20px',
                            color: '#7F8FA4',
                          }}
                          className="m-b-0 text-muted"
                        >
                          Password must be at least 8 characters long, including
                          at least one lowercase letter, one uppercase letter,
                          and one number.
                        </p>
                      </strong>
                    </div>
                  }
                  id="tooltip"
                >
                  <div>
                    <QuestionBlueIcon />
                  </div>
                </CustomTooltip>
              </Col>
              <Col xs={10}>
                <Field
                  customClass="m-0"
                  id="password"
                  name="password"
                  placeholder="Enter Password"
                  type={inputType(showPassword)}
                  component={FieldGroup}
                />
              </Col>
              <Col xs={2}>
                <Button
                  bsStyle="info"
                  className="btn-password btn btn-default"
                  onClick={() => setShowPassword(!showPassword)}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <ControlLabel>Password Confirmation</ControlLabel>
              </Col>
              <Col xs={12}>
                <Field
                  id="passwordConfirmation"
                  name="passwordConfirmation"
                  placeholder="Repeat Password"
                  type={inputType(showPassword)}
                  component={FieldGroup}
                />
              </Col>
            </Row>
          </Col>
          <Col xs={5} xsOffset={1}>
            <h3 className="flex m-b-28">Profile image</h3>
            <FormGroup className="flex flex-col flex-align-start">
              <ControlLabel>Upload</ControlLabel>
              <div className="m-b-8 w-100">
                <label htmlFor="avatar" className="file-input">
                  <UploadIcon />
                  <span className="m-l-4">Upload</span>
                  <input
                    style={{ display: 'none' }}
                    ref={inputRef}
                    id="avatar"
                    type="file"
                    accept=".jpeg, .png, .jpg"
                    onChange={handleImageSelect}
                  />
                </label>
                <div className="file-name m-t-8">{fileName}</div>
              </div>
              {submitFailed && errors.avatar && (
                <FormGroup validationState="error">
                  <HelpBlock>{errors.avatar}</HelpBlock>
                </FormGroup>
              )}
              <p className="m-0 text-muted">
                Allowed formats: .jpeg, .png, .jpg
              </p>
              <p className="text-muted">Max file size: 10Mb</p>
            </FormGroup>
          </Col>
        </Row>
      ) : null}

      <Row className="m-b-48">
        <Col xs={12}>
          {submitFailed && submitError ? (
            <FormError submitError={submitError} />
          ) : null}
        </Col>
        <Col xs={12}>
          <div className="flex">
            <div>
              <OrganizationUsersLink>
                <Button bsStyle="default">Cancel</Button>
              </OrganizationUsersLink>
              <Button
                className="m-l-12"
                bsStyle="primary"
                disabled={submitting}
                type="submit"
              >
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default UserForm;
